/**
 * Custom hook for fetching and caching organisation entities mapping
 * @module hooks/useOrganisationMapping
 */

import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

/**
 * Hook to fetch and cache organisation entities mapping
 * @returns {object} - Object containing organisationMapping, isLoading, error, retryCount, and hasData
 */
export const useOrganisationMapping = () => {
  const [organisationMapping, setOrganisationMapping] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const hasFetchedRef = useRef(false);
  const retryCountRef = useRef(0);
  const maxRetries = 3;

  const currentLang = useSelector((state) => state.intl.locale);
  const token = useSelector((state) => state.userSession?.token);

  useEffect(() => {
    if (Object.keys(organisationMapping).length > 0) {
      return;
    }

    if (isLoading) {
      return;
    }

    if (error && retryCountRef.current >= maxRetries) {
      return;
    }

    if (hasFetchedRef.current && !error) {
      return;
    }

    hasFetchedRef.current = true;

    setIsLoading(true);
    setError(null);

    fetchOrganisationEntities()
      .then((mapping) => {
        setOrganisationMapping(mapping);
        setIsLoading(false);
        retryCountRef.current = 0;
      })
      .catch((err) => {
        retryCountRef.current += 1;
        setError(err);
        setIsLoading(false);

        if (retryCountRef.current < maxRetries) {
          hasFetchedRef.current = false;
        }
      });

    async function fetchOrganisationEntities() {
      try {
        const endpoints = [
          '/@vocabularies/fhnw.vocabularies.organisation_entities',
          '/++api++/@vocabularies/fhnw.vocabularies.organisation_entities',
          '/@sources/organisation_entities',
        ];

        let allItems = [];

        for (const endpoint of endpoints) {
          try {
            const headers = {
              Accept: 'application/json',
              'Content-Type': 'application/json',
            };

            if (token) {
              headers.Authorization = `Bearer ${token}`;
            }

            // Fetch all pages with pagination
            let currentPage = 0;
            let hasMorePages = true;

            while (hasMorePages) {
              const paginatedUrl = `${endpoint}?b_start=${currentPage * 25}&b_size=25`;
              const response = await fetch(paginatedUrl, { headers });

              if (response.ok) {
                const responseData = await response.json();

                // Handle different response formats
                let items = [];
                if (responseData.items) {
                  items = responseData.items;
                } else if (responseData.terms) {
                  items = responseData.terms;
                } else if (Array.isArray(responseData)) {
                  items = responseData;
                }

                allItems = allItems.concat(items);

                const totalItems = responseData.items_total || items.length;
                const currentTotal = allItems.length;
                hasMorePages = currentTotal < totalItems && items.length > 0;

                currentPage++;
              } else {
                if (response.status >= 500) {
                  throw new Error(
                    `Server error ${response.status}: ${response.statusText}`,
                  );
                }
                break;
              }
            }

            if (allItems.length > 0) {
              break;
            }
          } catch (endpointError) {
            // Continue to next endpoint
          }
        }

        if (allItems.length === 0) {
          throw new Error(
            'All API endpoints failed - no organisation data available',
          );
        }

        // Transform vocabulary data into mapping object
        const mapping = {};

        allItems.forEach((item, index) => {
          const token = item.token || item.value || item.id;
          const title = item.title || item.label || item.name;

          if (token && title) {
            mapping[token] = title;
          }
        });

        return mapping;
      } catch (err) {
        throw err;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLang, token]);

  return {
    organisationMapping,
    isLoading,
    error: error && retryCountRef.current >= maxRetries ? error : null,
    retryCount: retryCountRef.current,
    hasData: Object.keys(organisationMapping).length > 0,
  };
};
